import React from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, Lock, ShoppingBag, Truck } from "lucide-react";
import { useCart } from "../context/CartContext";
import { submitShopifyCheckout, gbp, FREE_SHIP_PENCE, API_URL } from "../lib/shop";

export default function CartDrawer() {
  const { items, isOpen, closeCart, setQty, remove, count, subtotal, clear } = useCart();
  const [emailCaptured, setEmailCaptured] = React.useState(false);

  // the 10% welcome code only applies to customers who signed up with their email —
  // re-check each time the drawer opens so the fine print never over-promises
  React.useEffect(() => {
    if (isOpen) setEmailCaptured(!!localStorage.getItem("01l_email"));
  }, [isOpen]);

  const remaining = Math.max(0, FREE_SHIP_PENCE - subtotal);
  const progress = Math.min(100, (subtotal / FREE_SHIP_PENCE) * 100);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[80]" data-testid="cart-drawer-container">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={closeCart}
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 top-0 h-full w-full max-w-md bg-coal border-l border-white/10 flex flex-col"
            data-lenis-prevent
          >
            <div className="flex items-center justify-between px-6 h-16 border-b border-white/10 shrink-0">
              <span className="font-display font-bold uppercase tracking-tight flex items-center gap-3">
                YOUR CART
                <span className="font-mono text-xs text-ash">({count})</span>
              </span>
              <button onClick={closeCart} className="p-2 hover:opacity-60 transition-opacity" aria-label="Close cart" data-testid="cart-drawer-close-button">
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>

            {items.length > 0 && (
              <div className="px-6 py-4 border-b border-white/10 shrink-0" data-testid="cart-shipping-progress-bar">
                <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-ash mb-2.5 flex items-center gap-2">
                  <Truck className="w-3.5 h-3.5" />
                  {remaining > 0 ? (
                    <>Add <span className="text-bone">{gbp(remaining)}</span> for free UK delivery</>
                  ) : (
                    <span className="text-bone">Free UK delivery unlocked</span>
                  )}
                </p>
                <div className="h-1 bg-white/10 overflow-hidden">
                  <motion.div
                    className="h-full bg-ember"
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-6" data-testid="cart-items-list">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-5 py-16">
                  <ShoppingBag className="w-10 h-10 text-ash" strokeWidth={1} />
                  <p className="font-mono text-xs tracking-[0.18em] text-ash uppercase">Your cart is empty</p>
                  <Link
                    to="/shop"
                    onClick={closeCart}
                    className="bg-bone text-ink font-mono text-[11px] tracking-[0.2em] px-8 py-3.5 hover:bg-ember hover:text-white transition-colors duration-300"
                    data-testid="cart-empty-shop-button"
                  >
                    SHOP THE RANGE
                  </Link>
                </div>
              ) : (
                <ul className="divide-y divide-white/10">
                  {items.map((i) => (
                    <li key={i.variantId} className="py-5 flex gap-4" data-testid={`cart-item-${i.handle}`}>
                      <div className="w-20 h-24 bg-card overflow-hidden shrink-0">
                        <img src={i.image} alt={i.title} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-bold uppercase text-xs tracking-tight leading-snug">{i.title}</p>
                        <p className="font-mono text-[10px] text-ash tracking-[0.12em] uppercase mt-1">
                          {i.colour} / {i.size}
                          {i.preorder && <span className="text-ember ml-2">PREORDER</span>}
                        </p>
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center border border-white/15">
                            <button onClick={() => setQty(i.variantId, i.qty - 1)} className="p-2 hover:bg-white/5" aria-label="Decrease quantity" data-testid="cart-item-qty-decrease">
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="px-3 font-mono text-xs w-8 text-center">{i.qty}</span>
                            <button onClick={() => setQty(i.variantId, i.qty + 1)} className="p-2 hover:bg-white/5" aria-label="Increase quantity" data-testid="cart-item-qty-increase">
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                          <span className="font-mono text-sm">{gbp(i.price * i.qty)}</span>
                        </div>
                      </div>
                      <button onClick={() => remove(i.variantId)} className="p-1.5 self-start text-ash hover:text-ember transition-colors" aria-label="Remove item" data-testid="cart-item-remove-button">
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-white/10 px-6 py-5 shrink-0 bg-ink/40">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-[11px] tracking-[0.16em] uppercase text-ash">Subtotal</span>
                  <span className="font-display font-bold text-lg" data-testid="cart-subtotal">{gbp(subtotal)}</span>
                </div>
                <p className="font-mono text-[9px] tracking-[0.1em] text-ash uppercase mb-4" data-testid="cart-fine-print">
                  {emailCaptured ? "10% code & shipping applied at checkout" : "Shipping calculated at checkout"}
                </p>
                <button
                  onClick={() => {
                    const em = localStorage.getItem("01l_email");
                    if (em) {
                      fetch(`${API_URL}/cart-converted`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ email: em }),
                      }).catch(() => {});
                    }
                    submitShopifyCheckout(items);
                  }}
                  className="w-full flex items-center justify-center gap-2.5 bg-bone text-ink font-mono text-xs tracking-[0.22em] py-4 hover:bg-ember hover:text-white transition-colors duration-300"
                  data-testid="cart-checkout-button"
                >
                  <Lock className="w-3.5 h-3.5" /> CHECKOUT — {gbp(subtotal)}
                </button>
                <div className="flex items-center justify-between mt-3">
                  <button onClick={clear} className="font-mono text-[9px] tracking-[0.14em] text-ash hover:text-bone uppercase transition-colors" data-testid="cart-clear-button">
                    Clear cart
                  </button>
                  <span className="font-mono text-[9px] tracking-[0.14em] text-ash uppercase">Secure checkout via Shopify</span>
                </div>
              </div>
            )}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}
